import { useEffect, useRef, useState } from 'react';

import useClientState from '@/state/useClientState';

import { mwdWerklijnHeightPx } from '../utils/timescale';

import useWerklijnen from './useWerklijnen';

/** How many werklijnen to add per batch when scrolling */
const BATCH_SIZE = 50;

/**
 * Progressive rendering hook for werklijnen.
 *
 * Instead of rendering all 200+ werklijnen at once, this hook returns a
 * growing slice. It starts with the first 50 werklijnen, then adds 50
 * more as the user scrolls down. The count only grows (never shrinks),
 * so scrolling back up is instant — everything already rendered stays
 * in the DOM.
 *
 * Used by Werklijnen, LegendLeftContainer, and LegendRightContainer to
 * keep them in sync.
 */
const useProgressiveWerklijnen = () => {
  const { werklijnen, mwd, miniWerklijnen } = useWerklijnen();
  const scrollTop = useClientState((state) => state.scrollTop ?? 0);

  // Start with the first batch
  const [renderedCount, setRenderedCount] = useState(BATCH_SIZE);
  const renderedCountRef = useRef(BATCH_SIZE);

  useEffect(() => {
    // When werklijnen change (new search, sort, filter), reset to first batch
    setRenderedCount(BATCH_SIZE);
    renderedCountRef.current = BATCH_SIZE;
  }, [werklijnen.length]);

  useEffect(() => {
    // Calculate how many werklijnen are needed based on scroll position
    const neededCount = Math.ceil((scrollTop + window.innerHeight) / mwdWerklijnHeightPx) + BATCH_SIZE;

    if (neededCount > renderedCountRef.current) {
      // Round up to the next batch of 50
      const newCount = Math.min(Math.ceil(neededCount / BATCH_SIZE) * BATCH_SIZE, werklijnen.length);
      renderedCountRef.current = newCount;
      setRenderedCount(newCount);
    }
  }, [scrollTop, werklijnen.length]);

  // Slice werklijnen to the rendered count — only grows, never shrinks
  const visibleWerklijnen = werklijnen.length <= BATCH_SIZE ? werklijnen : werklijnen.slice(0, renderedCount);

  return {
    werklijnen: visibleWerklijnen,
    allWerklijnen: werklijnen,
    miniWerklijnen,
    mwd,
  };
};

export default useProgressiveWerklijnen;
